import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Loader2, ShieldCheck, KeyRound, User, Copy } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { auth, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TABS = [{ id: "admin", label: "Admin" }, { id: "account", label: "Collaboratore" }, { id: "register", label: "Registrati" }];

export default function Login() {
  const { loginAdmin, loginUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("admin");
  const [loading, setLoading] = useState(false);
  const [pw, setPw] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "admin") { await loginAdmin(pw); toast.success("Accesso admin effettuato"); navigate("/"); }
      else if (tab === "account") { await loginUser(email, pw); toast.success("Accesso effettuato"); navigate("/"); }
      else {
        const r = await auth.register(email, pw, name);
        setCode(r.approvalCode);
        toast.success("Account creato: in attesa di approvazione");
      }
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const inputC = "rounded-xl border-white/10 bg-[#0B0C10] px-4 py-3 text-white placeholder:text-slate-600 focus-visible:border-[#5865F2] focus-visible:ring-2 focus-visible:ring-[#5865F2]/30";
  const lbl = "mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-slate-400";

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0B0C10] p-4">
      <div className="pointer-events-none absolute -top-24 right-1/4 h-80 w-80 rounded-full bg-[#5865F2]/25 blur-3xl" style={{ animation: "floaty 9s ease-in-out infinite" }} />
      <div className="pointer-events-none absolute bottom-0 left-1/4 h-72 w-72 rounded-full bg-[#00F0FF]/10 blur-3xl" style={{ animation: "floaty 11s ease-in-out infinite" }} />

      <div className="relative w-full max-w-md animate-fade-up rounded-2xl border border-white/10 bg-[#12141D]/90 p-8 shadow-2xl shadow-black/80 backdrop-blur-2xl">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#5865F2] to-[#8B5CF6] shadow-[0_0_45px_rgba(88,101,242,0.6)]"><Bot className="h-8 w-8 text-white" /></div>
          <h1 className="text-3xl font-extrabold tracking-tight text-white">Command Center</h1>
          <p className="mt-1.5 text-sm text-slate-400">Gestione avanzata del tuo server Discord</p>
        </div>

        <div className="mb-6 flex rounded-xl bg-[#0B0C10] p-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => { setTab(t.id); setCode(""); }} data-testid={`login-tab-${t.id}`}
              className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-all ${tab === t.id ? "bg-[#5865F2] text-white shadow-lg shadow-[#5865F2]/25" : "text-slate-400 hover:text-white"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {code ? (
          <div className="space-y-4 text-center">
            <p className="text-sm text-slate-300">Fornisci questo codice all'amministratore per essere abilitato:</p>
            <div className="flex items-center justify-center gap-2 rounded-xl border border-[#5865F2]/40 bg-[#5865F2]/10 px-4 py-4">
              <span className="font-mono text-2xl font-bold tracking-widest text-white" data-testid="approval-code">{code}</span>
              <button onClick={() => { navigator.clipboard?.writeText(code); toast.success("Copiato"); }} className="text-slate-400 hover:text-white"><Copy className="h-4 w-4" /></button>
            </div>
            <Button onClick={() => { setTab("account"); setCode(""); }} className="w-full rounded-xl bg-white/5 text-white hover:bg-white/10">Torna al login</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {tab === "register" && (
              <div><label className={lbl}>Nome</label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Come ti chiami" data-testid="register-name-input" className={inputC} /></div>
            )}
            {(tab === "account" || tab === "register") && (
              <div><label className={lbl}>Email</label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" data-testid="login-email-input" className={inputC} /></div>
            )}
            <div>
              <label className={lbl}>Password{tab === "admin" ? " amministratore" : ""}</label>
              <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" data-testid="login-password-input" className={inputC} />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full rounded-xl bg-gradient-to-r from-[#5865F2] to-[#7289DA] py-3 font-bold text-white shadow-lg shadow-[#5865F2]/30 transition-all hover:from-[#4752C4] hover:to-[#5865F2] active:scale-[0.98]">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : tab === "admin" ? <KeyRound className="mr-2 h-4 w-4" /> : <User className="mr-2 h-4 w-4" />}
              {tab === "register" ? "Crea account" : "Entra"}
            </Button>
          </form>
        )}
        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500"><ShieldCheck className="h-3.5 w-3.5 text-[#23A55A]" /> Accesso protetto · RBAC</div>
      </div>
    </div>
  );
}
